import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import LoanExpensesEditor from './LoanExpensesEditor';
import DisplayField, { FieldGroup, FIELD_LABEL, fieldInputClass } from '../UI/DisplayField';
import { CashIcon, DocumentIcon } from '../UI/IconLibrary';
import { LoanSection, LOAN_GROUPS_GRID, PayoutGroup, RepaymentGroup } from './LoanSections';
import CalculatorDeclineBanner, { useLoanCalculator, RiskReasonField, RecalculateCalculation } from '../CalculatorDecline';
import DocumentFlagsBanner, { useLoanDocumentFlags } from '../DocumentFlagsBanner';
import { toCurrency } from '../../utils/adminMetrics';
import { deductionDayFromDates } from '../../utils/dateUtils';
import FullScreenPanel from '../UI/FullScreenPanel';
import DocumentTile, { DOCUMENT_GRID } from '../UI/DocumentTile';
import { startRecalculation } from './suggestedTerms';

// A field's label with a small action at its end (e.g. Reset, Use N$…), so
// the field keeps the same size as on the loan's details.
const LabelAction = ({ label, action, onAction, title }) => (
  <div className="flex items-baseline justify-between gap-2">
    <label className={`${FIELD_LABEL} min-w-0`}>{label}</label>
    {action && (
      <button
        type="button"
        onClick={onAction}
        title={title}
        className="flex-shrink-0 mb-1 text-[11px] font-medium text-blue-600 dark:text-blue-300 hover:underline whitespace-nowrap"
      >
        {action}
      </button>
    )}
  </div>
);

const LoanEditForm = ({ loan, onClose, onSaved, mode }) => {
  const [editForm, setEditForm] = useState({});
  // The loan's terms as loaded, to tell what was changed.
  const [saved, setSaved] = useState(null);
  // The loan as just loaded (payout, penalties, what is due): shown as on
  // the loan's details.
  const [current, setCurrent] = useState(null);
  // The calculator's assessment of the loan: the quote checks the amount
  // against what it qualified the client for.
  const [assessmentId, setAssessmentId] = useState(null);
  const [editLoanDocuments, setEditLoanDocuments] = useState([]);
  const [editLoading, setEditLoading] = useState(true);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [interestRates, setInterestRates] = useState([]);
  const [loanReasons, setLoanReasons] = useState([]);
  const [hasLoanDetailsChanged, setHasLoanDetailsChanged] = useState(false);
  const [manualStartEdited, setManualStartEdited] = useState(false);
  const [manualDueEdited, setManualDueEdited] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    principal_amount_plus_interest: loan?.principal_amount_plus_interest,
    monthly_payment: loan?.monthly_payment,
    start_date: loan?.start_date ? new Date(loan.start_date) : null,
    due_date: loan?.due_date ? new Date(loan.due_date) : null,
    status: loan?.status,
  });

  // Add state for new uploads
  const [newBankStatement, setNewBankStatement] = useState(null);
  const [newPayslip, setNewPayslip] = useState(null);
  // Staff note fields (optional)
  // Expenses this loan carries. Loaded from the loan, edited freely, and saved
  // as a set when the form is submitted.
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [savedExpenseCodes, setSavedExpenseCodes] = useState([]);
  // The expenses as saved on the loan, with their amounts: shown until a change is worked out.
  const [savedExpenseRows, setSavedExpenseRows] = useState([]);
  const [expensesLoaded, setExpensesLoaded] = useState(false);
  // What the loan would cost with the changes, from the API
  // (GET /api/loans/quote), or the message it gave instead.
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');

  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('Staff Note');
  const [isInternal, setIsInternal] = useState(true);
  // A loan the calculator declined goes back to the client only with the
  // assessor's reason (risk_reason, staff only).
  // Bumped when the calculation is run again, to load the new result.
  const [calculatorRun, setCalculatorRun] = useState(0);
  const calculator = useLoanCalculator(loan, calculatorRun);
  const documentFlags = useLoanDocumentFlags(loan);
  const calculatorDeclined = !!calculator?.declined;
  const [riskReason, setRiskReason] = useState('');

  // Helpers for date formatting
  const toYMD = (date) => {
    if (!date) return '';
    const d = (date instanceof Date) ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseYMDToLocalDate = (ymd) => {
    if (!ymd || typeof ymd !== 'string') return null;
    const m = /^\d{4}-\d{2}-\d{2}$/.exec(ymd.trim());
    if (!m) return null;
    const [yearStr, monthStr, dayStr] = ymd.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const fetchData = async () => {
      setEditLoading(true);
      setEditError('');
      try {
        const token = localStorage.getItem('token');
        // Get loan details
          const loanRes = await axios.get(apiUrl(`/api/loans/${loan.id}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        // The deduction day the loan was made with: the loan does not store
        // it, so it is read back from its deduction dates.
        const savedDay = loanRes.data.deduction_day
          ?? deductionDayFromDates(loanRes.data.start_date, loanRes.data.due_date);
        const loaded = {
          principal_amount: loanRes.data.principal_amount,
          interest_rate: loanRes.data.interest_rate,
          duration_months: loanRes.data.duration_months,
          deduction_day: savedDay ?? '',
          due_date: toYMD(loanRes.data.due_date),
          start_date: toYMD(loanRes.data.start_date),
        };
        setEditForm(loaded);
        setSaved(loaded);
        setCurrent(loanRes.data);
        setAssessmentId(loanRes.data.credit_assessment_id || null);

        // Get loan documents (optional)
        try {
          // server route is mounted at /api/document
          const loanDocsRes = await axios.get(apiUrl(`/api/document/loan/${loan.id}`), {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Normalize loan documents response: server returns { documents: [...] }
          const loanDocsArray = Array.isArray(loanDocsRes.data)
            ? loanDocsRes.data
            : (Array.isArray(loanDocsRes.data?.documents) ? loanDocsRes.data.documents : []);

          // Get client identification docs (optional)
          try {
            const clientDocsRes = await axios.get(apiUrl(`/api/document/client/${loan.client_id}`), {
              headers: { Authorization: `Bearer ${token}` }
            });
            const clientDocsArray = Array.isArray(clientDocsRes.data)
              ? clientDocsRes.data
              : (Array.isArray(clientDocsRes.data?.documents) ? clientDocsRes.data.documents : []);
            const clientIdDocs = (clientDocsArray || []).filter(doc => doc.document_type === 'Identification');
            setEditLoanDocuments([...(loanDocsArray || []), ...clientIdDocs]);
          } catch (clientDocErr) {
            // If client docs fail, still set whatever loan docs we have
            setEditLoanDocuments(loanDocsArray || []);
            console.warn('Failed to fetch client documents for loan edit:', clientDocErr);
          }
        } catch (loanDocsErr) {
          // No documents is not fatal for editing; just log and continue
          setEditLoanDocuments([]);
          console.warn('Failed to fetch loan documents for edit:', loanDocsErr);
        }
      } catch (err) {
        // Only set failure if loan details couldn't be fetched
        setEditError('Failed to load loan.');
        console.error('Error loading loan details in LoanEditForm:', err);
      }
      setEditLoading(false);
    };
    fetchData();
  }, [loan]);

  // Keep Calculated Values dates in sync with manual date edits
  useEffect(() => {
    if (editForm.start_date === undefined && editForm.due_date === undefined) return;
    setCalculatedValues((cv) => ({
      ...cv,
      start_date: editForm.start_date ? parseYMDToLocalDate(editForm.start_date) : null,
      due_date: editForm.due_date ? parseYMDToLocalDate(editForm.due_date) : null,
    }));
  }, [editForm.start_date, editForm.due_date]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl('/api/interestRates'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInterestRates(res.data || []);
      } catch (err) {
        setInterestRates([]);
      }
    };
    fetchRates();
  }, []);

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-reasons'), { headers: { Authorization: `Bearer ${token}` } });
        setLoanReasons(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setLoanReasons([]);
      }
    };
    fetchReasons();
  }, []);

  // Load the expenses already on this loan so they can be added to or removed.
  useEffect(() => {
    let alive = true;
    if (!loan?.id) return undefined;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/additional-expenses`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!alive) return;
        const rows = Array.isArray(res.data?.expenses) ? res.data.expenses : [];
        const codes = rows.map((r) => r.expense_code).filter(Boolean);
        setExpenseCodes(codes);
        setSavedExpenseCodes(codes);
        setSavedExpenseRows(rows);
      } catch {
        if (alive) setExpenseCodes([]);
      } finally {
        if (alive) setExpensesLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [loan?.id]);

  // The amounts with the changes — principal + interest, the expenses and the
  // monthly payment — from the API's loan quote, the one place they are
  // worked out, so the preview matches what is saved. It includes the
  // expenses chosen below.
  // Worked out for the figures in the form at that moment (quotedFor); the
  // loan is saved only with figures for exactly what it holds. With the calculator's assessment, the
  // amount is also checked against what the client qualified for.
  const expenseKey = JSON.stringify(expenseCodes);
  const quoteInputs = JSON.stringify([editForm.principal_amount, editForm.interest_rate, editForm.duration_months, editForm.deduction_day, expenseKey]);
  const [quotedFor, setQuotedFor] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const recalculate = async () => {
    const p = parseFloat(editForm.principal_amount);
    const n = parseInt(editForm.duration_months, 10);
    setQuote(null);
    setQuotedFor(null);
    if (!p || !n || editForm.interest_rate === undefined || editForm.interest_rate === '') {
      setQuoteError('Enter the principal amount, interest rate and duration first.');
      return;
    }
    setQuoting(true);
    setQuoteError('');
    try {
      const params = {
        principal_amount: editForm.principal_amount,
        duration_months: n,
        interest_rate: editForm.interest_rate,
        additional_expenses: expenseKey,
      };
      if (loan?.client_id) params.client_id = loan.client_id;
      if (editForm.deduction_day) params.deduction_day = editForm.deduction_day;
      // The statutory maximum always applies (ML.S.4), declined or not: no
      // override can lift it.
      if (assessmentId) params.credit_assessment_id = assessmentId;
      const res = await axios.get(apiUrl('/api/loans/quote'), {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setQuote(res.data);
      setQuotedFor(quoteInputs);
    } catch (err) {
      setQuoteError(err.response?.data?.error || 'The loan amounts could not be worked out. Please try again.');
    } finally {
      setQuoting(false);
    }
  };

  const savedExpensesTotal = savedExpenseRows.reduce((sum, r) => sum + (Number(r.calculated_amount) || 0), 0);
  const expensesChanged = expensesLoaded
    && JSON.stringify([...expenseCodes].map(String).sort()) !== JSON.stringify([...savedExpenseCodes].map(String).sort());
  const showQuoted = hasLoanDetailsChanged || expensesChanged;
  // Changed, and the figures not (yet) worked out for exactly these values.
  const needsRecalc = showQuoted && (quotedFor !== quoteInputs || !quote);
  const freshQuote = showQuoted && !needsRecalc ? quote : null;
  // The figures follow the form: worked out again a moment after each change
  // (typing, "Use this amount", a new expense).
  useEffect(() => {
    if (!expensesLoaded || !showQuoted) return undefined;
    const timer = setTimeout(() => { recalculate(); }, 400);
    return () => clearTimeout(timer);
  }, [quoteInputs, showQuoted, expensesLoaded]);

  // The amount the calculator recommends for the term chosen: within the
  // recommended instalment (above it, up to the statutory maximum, needs an
  // officer's review). An older result without it: the statutory amount.
  const months = parseInt(editForm.duration_months, 10) || null;
  const byTerm = calculator?.recommended_by_term || calculator?.qualified_by_term || null;
  const qualifiedForTerm = months && byTerm ? Number(byTerm[months]) || null : null;
  const suggestion = calculator?.suggestion || null;
  // The amount Principal Amount offers to put in: the qualified one for this
  // term, whenever the amount is not already it (above it, too much; below
  // it, e.g. after a longer term); otherwise the calculator's suggestion,
  // with its term. Null when there is nothing to offer.
  const useAmount = (() => {
    if (qualifiedForTerm > 0) {
      const amount = Math.floor(qualifiedForTerm * 100) / 100;
      if (Math.round(Number(editForm.principal_amount) * 100) === Math.round(amount * 100)) return null;
      return { amount, title: `The calculator recommends up to ${toCurrency(amount)} over ${months} ${months === 1 ? 'month' : 'months'}` };
    }
    if (suggestion?.amount > 0) {
      return { amount: suggestion.amount, months: suggestion.months, title: `The calculator suggests ${toCurrency(suggestion.amount)} over ${suggestion.months} ${suggestion.months === 1 ? 'month' : 'months'}` };
    }
    return null;
  })();
  // The expenses list (add or remove one) is open.
  const [editingExpenses, setEditingExpenses] = useState(false);

  // When the loan asks for more than the client qualifies for, the
  // calculator's amount is put in by itself (and its term, when the loan's
  // term gives nothing): once for each calculator result (again after "Run
  // calculation again"), so staff can still change it. The figures and dates
  // then follow from the quote and the schedule.
  const autoFilled = useRef(null);
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  useEffect(() => {
    if (editLoading || !saved || !calculator) return;
    const resultKey = calculator.assessed_at || 'loaded';
    if (autoFilled.current === resultKey) return;
    autoFilled.current = resultKey;
    const asked = Number(saved.principal_amount) || 0;
    const term = parseInt(saved.duration_months, 10) || null;
    const terms = calculator.recommended_by_term || calculator.qualified_by_term;
    const qualified = term && terms ? Number(terms[term]) || 0 : 0;
    const target = qualified > 0
      ? (asked > qualified ? { amount: Math.floor(qualified * 100) / 100 } : null)
      : (calculator.suggestion?.amount > 0 && asked > calculator.suggestion.amount
        ? { amount: calculator.suggestion.amount, months: calculator.suggestion.months }
        : null);
    if (!target) return;
    setEditForm((prev) => ({
      ...prev,
      principal_amount: String(target.amount),
      ...(target.months ? { duration_months: String(target.months) } : {}),
    }));
    setSuggestionApplied(true);
  }, [calculator, saved, editLoading]);

  // The deduction dates for a deduction day and duration, from the API
  // (GET /api/loans/schedule): a month-end salary is deducted on each
  // month's last day.
  // With startDate (a start date staff typed in) the schedule runs from it and
  // gives the deduction day; otherwise from the deduction day, as at capture.
  async function fetchSchedule(deductionDay, months, startDate = null) {
    const d = parseInt(deductionDay, 10);
    const n = parseInt(months, 10);
    if (!n || (!startDate && !d)) return null;
    const res = await axios.get(apiUrl('/api/loans/schedule'), {
      params: startDate ? { start_date: startDate, duration_months: n } : { deduction_day: d, duration_months: n },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const start = res.data?.start_date || '';
    const due = res.data?.due_date || '';
    return {
      start_date_str: start,
      due_date_str: due,
      start_date: start ? parseYMDToLocalDate(start) : null,
      due_date: due ? parseYMDToLocalDate(due) : null,
      deduction_day: res.data?.deduction_day ?? null,
    };
  }

  // A start date typed in is the first deduction: the deduction day and the
  // final payment follow from it, as the schedule works them out.
  useEffect(() => {
    if (!manualStartEdited || !editForm.start_date || !editForm.duration_months) return undefined;
    let alive = true;
    fetchSchedule(null, editForm.duration_months, editForm.start_date)
      .then((sched) => {
        if (!alive || !sched) return;
        setManualDueEdited(false);
        setEditForm((prev) => ({
          ...prev,
          due_date: sched.due_date_str,
          deduction_day: sched.deduction_day != null ? String(sched.deduction_day) : prev.deduction_day,
        }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [editForm.start_date, manualStartEdited]);

  const handleResetDates = async () => {
    try {
      const calc = await fetchSchedule(editForm.deduction_day, editForm.duration_months);
      if (!calc) return;
      setManualStartEdited(false);
      setManualDueEdited(false);
      setEditForm(prev => ({ ...prev, start_date: calc.start_date_str, due_date: calc.due_date_str }));
      setCalculatedValues(cv => ({
        ...cv,
        start_date: calc.start_date,
        due_date: calc.due_date,
      }));
    } catch (_) {}
  };

  // Track if any loan detail field has changed from the original database values
  useEffect(() => {
    if (!loan || !saved) return setHasLoanDetailsChanged(false);
    const changed = ['principal_amount', 'interest_rate', 'duration_months', 'deduction_day']
      .some((k) => String(editForm[k] ?? '') !== String(saved[k] ?? ''));

    setHasLoanDetailsChanged(changed);

    if (changed) {
      setCalculatedValues(cv => ({ ...cv, status: loan.status }));
      // The dates, from the API, unless typed in by hand.
      fetchSchedule(editForm.deduction_day, editForm.duration_months, manualStartEdited ? editForm.start_date : null)
        .then((dates) => {
          if (!dates) return;
          setCalculatedValues(cv => ({ ...cv, start_date: dates.start_date, due_date: dates.due_date }));
          setEditForm(prev => ({
            ...prev,
            start_date: (!manualStartEdited || !prev.start_date) ? dates.start_date_str : prev.start_date,
            due_date: (!manualDueEdited || !prev.due_date) ? dates.due_date_str : prev.due_date,
          }));
        })
        .catch(() => {});
    } else {
      setCalculatedValues({
        principal_amount_plus_interest: loan.principal_amount_plus_interest,
        monthly_payment: loan.monthly_payment,
        start_date: loan.start_date ? new Date(loan.start_date) : null,
        due_date: loan.due_date ? new Date(loan.due_date) : null,
        status: loan.status,
      });
      // Keep existing form dates from loan
      setEditForm(prev => ({
        ...prev,
        start_date: toYMD(loan.start_date),
        due_date: toYMD(loan.due_date),
      }));
    }
  }, [
    editForm.principal_amount,
    editForm.interest_rate,
    editForm.duration_months,
    editForm.deduction_day,
    loan,
    saved
  ]);

  const handleEditFormChange = e => {
    if (e.target.name === 'deduction_day') setManualStartEdited(false);
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };


  const handleEditFormSubmit = async e => {
    e.preventDefault();
    if (needsRecalc) {
      setEditError(quoteError || 'The new figures are still being worked out. Save again in a moment.');
      document.getElementById('loan-edit-calculation')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setEditSaving(true);
    setEditError('');
    setEditMessage('');
    try {
      const token = localStorage.getItem('token');
      // 1. Update loan details — choose endpoint based on mode/status
      const useStaffReview = mode === 'staff-review' || loan.status === 'Awaiting Staff';
      if (useStaffReview) {
        // Require a staff note
        if (!noteText || !noteText.trim()) {
          setEditSaving(false);
          setEditError('Staff note is required.');
          return;
        }
      }
      if (useStaffReview) {
        // Call staff-review endpoint which also accepts a note
        const payload = {
          ...editForm,
          note_text: noteText || undefined,
          note_type: noteType || undefined,
          is_internal: !!isInternal,
          ...(calculatorDeclined ? { risk_reason: riskReason.trim() } : {}),
        };
        await axios.put(
          apiUrl(`/api/loans/${loan.id}/staff-review`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          apiUrl(`/api/loans/${loan.id}`),
          editForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // 1b. Save the expense selection. Its own request, so it applies whether
      // the details went to the staff-review endpoint or the ordinary one.
      if (expensesLoaded) {
        await axios.put(
          apiUrl(`/api/loans/${loan.id}/additional-expenses`),
          { expenses: expenseCodes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // 2. Upload new documents if provided
      // Bank Statement
      if (newBankStatement) {
        const formData = new FormData();
        formData.append('contract', newBankStatement);
        formData.append('loan_id', loan.id);
        formData.append('client_id', loan.client_id);
        formData.append('document_type', 'Bank Statement');
        await axios.post(
          apiUrl('/api/documents/upload'),
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      // Payslip
      if (newPayslip) {
        const formData = new FormData();
        formData.append('contract', newPayslip);
        formData.append('loan_id', loan.id);
        formData.append('client_id', loan.client_id);
        formData.append('document_type', 'Payslip');
        await axios.post(
          apiUrl('/api/documents/upload'),
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      // New terms: the affordability check tests the new instalment. A new
      // payslip or statement: it reads them.
      const rerun = (hasLoanDetailsChanged || expensesChanged || newBankStatement || newPayslip) && (assessmentId || loan.credit_assessment_id)
        ? await startRecalculation(loan.id)
        : null;

      setEditSaving(false);
      setEditMessage(rerun ? 'Loan updated successfully! The calculation is being run again.' : 'Loan updated successfully!');
      setTimeout(() => {
        if (onSaved) onSaved();
      }, 1200);
    } catch (err) {
      const apiMsg = err?.response?.data?.error || err?.message || 'Failed to update loan.';
      setEditError(apiMsg);
      setEditSaving(false);
    }
  };


  // The statement and payslip: shown with the newest one on file, and
  // replaceable (uploaded on save).
  const newestOf = (type) => [...editLoanDocuments]
    .filter((d) => String(d.document_type || '').toLowerCase() === type.toLowerCase())
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0) || (b.id || 0) - (a.id || 0))[0] || null;
  const REATTACH = [
    { type: 'Bank Statement', file: newBankStatement, set: setNewBankStatement },
    { type: 'Payslip', file: newPayslip, set: setNewPayslip },
  ];

  const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  const inputCls = (filled = true) => fieldInputClass(!filled);

  return (
    <FullScreenPanel
      isOpen
      onClose={onClose}
      title={`Edit loan${loan.clientName ? ` for ${loan.clientName}` : ''}`}
      subtitle={loan.loan_code || loan.loanCode || undefined}
      // Pinned under the form: the buttons, and what saving said, always in view.
      footer={editLoading ? null : (
        <div className="w-full flex flex-col gap-2">
          {editError && (
            <div role="alert" className="p-2.5 rounded border bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-300 border-red-200 dark:border-red-800/40">
              {editError}
            </div>
          )}
          {editMessage && (
            <div className="p-2.5 rounded border bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40">
              {editMessage}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <span className="mr-auto text-xs text-gray-500 dark:text-slate-400">
              {needsRecalc
                ? (quoteError ? 'Fix the loan figures before saving (see Amounts).' : 'Working out the new figures…')
                : (saved && ['principal_amount', 'interest_rate', 'duration_months', 'start_date', 'due_date'].some((k) => String(editForm[k] ?? '') !== String(saved[k] ?? ''))
                  && (mode === 'staff-review' || ['Awaiting Staff', 'Awaiting Approval'].includes(loan.status)) && loan.application_type !== 'Walk-in')
                  ? 'The new terms or dates go back to the client, who signs the agreement again.'
                  : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 font-semibold border border-gray-300 dark:border-slate-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="loan-edit-form"
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={editSaving || quoting || needsRecalc}
              title={needsRecalc ? (quoteError || 'Working out the new figures') : undefined}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
      headerRight={(
        <RecalculateCalculation
          compact
          loan={{ ...loan, credit_assessment_id: assessmentId ?? loan.credit_assessment_id }}
          calculator={calculator}
          onDone={() => setCalculatorRun((n) => n + 1)}
        />
      )}
    >
          {editLoading ? (
            <div className="p-8 text-center text-gray-600 dark:text-slate-400">Loading...</div>
          ) : (
            // The same sections, groups, order and labels as viewing the loan
            // (LoanDetails), with the terms as fields: the calculator, then
            // Loan Details (Loan, Amounts, Additional Expenses, Repayment,
            // Payout), then Documents; the staff note last.
            <form id="loan-edit-form" onSubmit={handleEditFormSubmit} className="space-y-4">
              <CalculatorDeclineBanner calculator={calculator} loan={{ ...loan, duration_months: editForm.duration_months || loan.duration_months }} suggestionApplied={suggestionApplied} allowOverride={false} />
              <DocumentFlagsBanner flags={documentFlags} />
              <LoanSection Icon={CashIcon} title="Loan Details" tone="yellow">
              <div id="loan-edit-calculation" className={`${LOAN_GROUPS_GRID} scroll-mt-4`}>
                <FieldGroup title="Loan" tone="blue" wide>
                  <DisplayField label="Loan Code">{loan.loan_code || loan.loanCode || '-'}</DisplayField>
                  <div>
                    <label className={FIELD_LABEL}>Loan Reason</label>
                    <select
                      name="loan_reason_id"
                      value={editForm.loan_reason_id || loan.loan_reason_id || ''}
                      onChange={handleEditFormChange}
                      className={inputCls()}
                    >
                      <option value="">Select reason</option>
                      {loanReasons.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    {/* The first deduction: the deduction day follows from it. */}
                    <LabelAction
                      label="Start Date"
                      action={manualStartEdited || manualDueEdited ? 'Reset' : null}
                      onAction={handleResetDates}
                      title="Work the start and due dates out again from the deduction day"
                    />
                    <input
                      type="date"
                      name="start_date"
                      value={editForm.start_date || ''}
                      onChange={(e) => {
                        setManualStartEdited(true);
                        setEditForm(prev => ({ ...prev, start_date: e.target.value }));
                      }}
                      className={inputCls()}
                    />
                  </div>
                  <div>
                    <label className={FIELD_LABEL}>Due Date</label>
                    <input
                      type="date"
                      name="due_date"
                      value={editForm.due_date || ''}
                      min={editForm.start_date || undefined}
                      onChange={(e) => {
                        setManualDueEdited(true);
                        setEditForm(prev => ({ ...prev, due_date: e.target.value }));
                      }}
                      className={inputCls()}
                    />
                  </div>
                  <div>
                    <label className={FIELD_LABEL}>Duration (months)</label>
                    <select
                      name="duration_months"
                      value={editForm.duration_months || ''}
                      onChange={handleEditFormChange}
                      className={inputCls(isFilled(editForm.duration_months))}
                      required
                    >
                      <option value="">Select duration</option>
                      <option value="1">1 Month</option>
                      <option value="2">2 Months</option>
                      <option value="3">3 Months</option>
                      <option value="4">4 Months</option>
                      <option value="5">5 Months</option>
                      <option value="6">6 Months</option>
                    </select>
                  </div>
                  <div>
                    <label className={FIELD_LABEL}>Interest Rate</label>
                    <select
                      name="interest_rate"
                      value={
                        editForm.interest_rate !== undefined && editForm.interest_rate !== null
                          ? String(editForm.interest_rate)
                          : (loan && loan.interest_rate !== undefined && loan.interest_rate !== null
                              ? String(loan.interest_rate)
                              : '')
                      }
                      onChange={handleEditFormChange}
                      className={inputCls(isFilled(editForm.interest_rate !== undefined && editForm.interest_rate !== null ? editForm.interest_rate : loan?.interest_rate))}
                      required
                    >
                      <option value="">Select interest rate</option>
                      {Array.isArray(interestRates) && interestRates.length > 0 ? (
                        interestRates.map(rateObj => (
                          <option key={rateObj.id} value={String(rateObj.rate)}>
                            {rateObj.rate}%
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No interest rates found
                        </option>
                      )}
                    </select>
                  </div>
                </FieldGroup>

                {/* The figures: the new ones once worked out (the API's quote),
                    otherwise the loan's as saved. */}
                <FieldGroup title="Amounts" tone="emerald">
                  <div>
                    {/* What the calculator qualified the client for (or, when that
                        is nothing for this term, its suggestion): one click puts
                        it in, then the figures are worked out again. */}
                    <LabelAction
                      label="Principal Amount"
                      action={useAmount ? `Use ${toCurrency(useAmount.amount)}` : null}
                      onAction={() => setEditForm((prev) => ({
                        ...prev,
                        principal_amount: String(useAmount.amount),
                        ...(useAmount.months ? { duration_months: String(useAmount.months) } : {}),
                      }))}
                      title={useAmount?.title}
                    />
                    <input
                      type="number"
                      name="principal_amount"
                      value={editForm.principal_amount || ''}
                      onChange={handleEditFormChange}
                      className={inputCls(isFilled(editForm.principal_amount))}
                      required
                    />
                  </div>
                  <DisplayField label="Principal + Interest">
                    {showQuoted
                      ? (freshQuote ? toCurrency(Number(freshQuote.principal_amount_plus_interest)) : null)
                      : (calculatedValues?.principal_amount_plus_interest != null ? toCurrency(parseFloat(calculatedValues.principal_amount_plus_interest)) : null)}
                  </DisplayField>
                  <DisplayField label="Base Loan Obligation" tone="strong">
                    {showQuoted
                      ? (freshQuote ? toCurrency(Number(freshQuote.total_repayable)) : null)
                      : toCurrency((Number(calculatedValues?.principal_amount_plus_interest) || 0) + savedExpensesTotal)}
                  </DisplayField>
                  {showQuoted && needsRecalc && !quoteError && (
                    <p className="col-span-full text-xs text-gray-500 dark:text-slate-400">Working out the new figures…</p>
                  )}
                  {showQuoted && quoteError && (
                    <p className="col-span-full text-xs text-rose-600 dark:text-rose-400">{quoteError}</p>
                  )}
                </FieldGroup>

                {/* As on the loan's details: each expense and the total. Change
                    opens the list to add or remove one. */}
                <FieldGroup
                  tone="amber"
                  title={(
                    <span className="flex items-center justify-between gap-2">
                      Additional Expenses
                      {expensesLoaded && (
                        <button
                          type="button"
                          onClick={() => setEditingExpenses((v) => !v)}
                          className="normal-case tracking-normal text-[11px] font-medium text-blue-600 dark:text-blue-300 hover:underline"
                        >
                          {editingExpenses ? 'Done' : 'Change'}
                        </button>
                      )}
                    </span>
                  )}
                >
                  {!expensesLoaded ? (
                    <DisplayField label="Additional Expenses">Loading…</DisplayField>
                  ) : (
                    <>
                      {(showQuoted ? (freshQuote?.expenses || []) : savedExpenseRows).map((expense) => (
                        <DisplayField key={expense.id || expense.expense_code} label={expense.expense_name || expense.expense_code}>
                          {toCurrency(Number(expense.calculated_amount || 0))}
                        </DisplayField>
                      ))}
                      <DisplayField label="Total Additional Expenses">
                        {showQuoted
                          ? (freshQuote ? toCurrency(Number(freshQuote.expenses_total || 0)) : null)
                          : toCurrency(savedExpensesTotal)}
                      </DisplayField>
                      {editingExpenses && (
                        <div className="col-span-full">
                          <LoanExpensesEditor
                            value={expenseCodes}
                            onChange={setExpenseCodes}
                            priced={showQuoted ? (freshQuote?.expenses ?? null) : savedExpenseRows}
                            total={showQuoted ? (freshQuote?.expenses_total ?? null) : savedExpensesTotal}
                          />
                        </div>
                      )}
                    </>
                  )}
                </FieldGroup>

                {/* Total due is the API's figure: worked out again once saved. */}
                <RepaymentGroup
                  loan={current || loan}
                  monthly={showQuoted
                    ? (freshQuote ? toCurrency(Number(freshQuote.monthly_payment)) : null)
                    : (calculatedValues?.monthly_payment != null ? toCurrency(parseFloat(calculatedValues.monthly_payment)) : null)}
                  totalDue={showQuoted
                    ? 'Worked out on saving'
                    : (current?.total_due !== undefined && current?.total_due !== null ? toCurrency(Number(current.total_due)) : null)}
                />

                <PayoutGroup loan={current || loan} />
              </div>
              </LoanSection>

              {/* Documents Section - show for Walk-in loans even during staff-review; otherwise hide for staff-review edits */}
              {(loan?.application_type === 'Walk-in' || !(mode === 'staff-review' || loan.status === 'Awaiting Staff')) && (
              <LoanSection Icon={DocumentIcon} title="Documents">
                <div className={DOCUMENT_GRID}>
                  {editLoanDocuments
                    .filter((doc) => !REATTACH.some((r) => r.type.toLowerCase() === String(doc.document_type || '').toLowerCase()))
                    .map((doc) => (
                      <DocumentTile key={doc.id || doc.file_path} type={doc.document_type} doc={doc} />
                    ))}
                  {REATTACH.map((r) => (
                    <DocumentTile
                      key={r.type}
                      type={r.type}
                      doc={newestOf(r.type)}
                      file={r.file}
                      onPick={(f) => r.set(f)}
                      onRemove={() => r.set(null)}
                    />
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">A new bank statement or payslip (PDF, max 10 MB) is uploaded when you save.</p>
              </LoanSection>
              )}

                {/* Staff Note - only visible during staff-review */}
                {(mode === 'staff-review' || loan.status === 'Awaiting Staff') && (
                  <LoanSection Icon={DocumentIcon} title="Staff Note">
                    <div className="mb-4">
                      <label className={FIELD_LABEL}>Note</label>
                      <textarea
                        name="noteText"
                        value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        rows={4}
                        className={inputCls(isFilled(noteText))}
                        required
                      />
                      {calculatorDeclined && <RiskReasonField value={riskReason} onChange={setRiskReason} />}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <div>
                        <label className={FIELD_LABEL}>Note Type</label>
                        <select
                          value={noteType}
                          onChange={e => setNoteType(e.target.value)}
                          className={inputCls()}
                        >
                          <option>Staff Note</option>
                          <option>Client Message</option>
                          <option>Internal</option>
                          <option value="AcknowledgementOfDebt">Acknowledgement of debt</option>
                          <option value="Summons">Summons</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <input type="checkbox" id="isInternal" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="mr-2" />
                        <label htmlFor="isInternal" className="text-sm text-gray-700 dark:text-slate-300">Internal (not visible to client)</label>
                      </div>
                    </div>
                  </LoanSection>
                )}
            </form>
          )}
    </FullScreenPanel>
  );
};

export default LoanEditForm;
